import React, {useState, useEffect, useCallback} from 'react';
import {Box, ClickAwayListener, FormLabel, MenuItem, Paper, TextField, Typography} from "@mui/material";
import {EnvironmentConfig} from "../../common/configs/environment.config";
import {useController} from "react-hook-form";
import {GooglePlace} from "../../common/types/googlePlace.type";
import {GooglePlacesInputProps} from "../../common/types/googlePlacesInputProps.type";
import {FieldPath, FieldValues} from "react-hook-form/dist/types";
import useGoogle from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import {useDebounceValue} from "../../common/hooks/useDebounce";

const GooglePlacesInput = <T extends FieldValues, TName extends FieldPath<T>>(props: GooglePlacesInputProps<T, TName>) => {
    const {
        label,
        hint,
        errorMessage,
        error,
        focused,
        onFocus,
        placeholder,
        addressSelected,
        onBlur,
        sx,
        ...rest
    } = props;

    const {control, name, rules, defaultValue, shouldUnregister, ...inputRest} = rest;

    const {field} = useController({
        control,
        name,
        rules,
        defaultValue,
        shouldUnregister
    });

    const [inputValue, setInputValue] = useState<string | "">("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchTerm = useDebounceValue(inputValue, 500);

    const {
        placePredictions,
        getPlacePredictions,
        placesService
    } = useGoogle({
        apiKey: EnvironmentConfig.MapsApiKey,
        debounce: 400, // This will debounce API calls internally if supported,
        sessionToken: true,
        options: {
            input: searchTerm,
            types: ['(regions)'],
        },
    });

    useEffect(() => {
        getPlacePredictions({input: searchTerm, types: ['(regions)']});
    }, [searchTerm]);

    const getAddressString = useCallback((place: GooglePlace | undefined) => {
        let text = "";
        if (!place) {
            return text;
        }
        
        if(place.city)
        {
            text += `${place.city}, `
        }
        if(place.state)
        {
            text += `${place.state}, `
        }
        if(place.countryCode)
        {
            text += `${place.countryCode}`
        }
        
        return text;
    }, []);

    useEffect(() => {
        const address = field.value as GooglePlace | undefined;
        setInputValue(getAddressString(address));
    }, [field.value, getAddressString])

    const onChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
        //if text fully deleted consider this a clear
        if (!ev.target.value) {
            field.onChange(null);
        }
        setInputValue(ev.target.value);
    };
    
    const onPlaceResult = (place: any | null) => {

        //Searching without making a selection and pressing enter runs this logic
        // however the object only contains the search term which results in undefined text
        if (!place || (place && !place.hasOwnProperty("address_components"))) {
            return;
        }

        const city = place.address_components?.find((c: any) => c.types.some((x: any) => x === "locality"))?.long_name;
        const state = place.address_components?.find((c: any) => c.types.some((x: any) => x === "administrative_area_level_1"))?.short_name;
        const country = place.address_components?.find((c: any) => c.types.some((x: any) => x === "country"))?.long_name;

        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        const address = {
            city: city,
            state: state,
            countryCode: country,
            lat: lat,
            lng: lng
        } as GooglePlace;

        addressSelected?.(address);
        setInputValue(getAddressString(address));
        field.onChange(address);

        setShowSuggestions(false);
    }

    const onPlaceSelected = (place: any) => {
        placesService?.getDetails(
            {
                placeId: place.place_id,
            },
            onPlaceResult
        );
    };

    const handleClickAway = () => {
        setShowSuggestions(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box position={"relative"}>
                {label && (
                    <FormLabel id={`${name}-label`} className={"input-label"} sx={{mb: 0.5}}>
                        {label}
                    </FormLabel>
                )}
                <TextField
                    margin="none"
                    autoComplete='off'
                    onFocus={ev => {
                        setShowSuggestions(true);
                        onFocus?.(ev);
                    }}
                    value={inputValue}
                    onChange={onChanged}
                    fullWidth
                    placeholder={placeholder ?? ""}
                    id={name}
                    error={error}
                    InputProps={{
                        placeholder: "Start typing your address",
                    }}
                    {...inputRest}
                />
                {showSuggestions && placePredictions && placePredictions.length > 0 && (
                    <Paper
                        elevation={3}
                        sx={{
                            position: 'absolute',
                            top: '100%', // Position below the TextField
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            maxHeight: '200px',
                            overflow: 'auto', // Allow scrolling if needed
                            mt: '2px'
                        }}
                    >
                        {placePredictions.map((suggestion, index) => (
                            <MenuItem key={index} onClick={() => onPlaceSelected(suggestion)}>
                                {suggestion.description}
                            </MenuItem>
                        ))}
                    </Paper>
                )}
                {hint &&
                    <Typography component="div" variant={"body2"} className={"input-help"}>
                        {hint}
                    </Typography>
                }
                {errorMessage &&
                    <Typography component="div" variant={"body2"} className={"error-message"}>
                        {errorMessage}
                    </Typography>
                }
            </Box>
        </ClickAwayListener>
    )
};

export {GooglePlacesInput};
